import React, { useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiError, formatIndian } from "@/lib/utils";
import { Download, Upload, ImagePlus, FileSpreadsheet, CheckCircle2, AlertCircle, X, ExternalLink } from "lucide-react";

// Master-admin only page that hosts:
//   1. Excel bulk upload (preview → confirm)
//   2. Bulk photo upload (filename match + optional mapping sheet)
//   3. Download Excel template
export default function CatalogUpload() {
  return (
    <div className="space-y-8" data-testid="catalog-upload-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A84C] mb-1">Distribution Module</p>
        <h1 className="font-serif text-4xl text-[#0D1B2A]">Catalog Upload</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Upload planned catalog designs in bulk (Excel) and attach photos by Product Code. This is separate from the existing Jewel Code uploader and feeds the Distribution links only.
        </p>
      </header>

      <Tabs defaultValue="excel" className="space-y-6">
        <TabsList className="bg-white border border-[#0D1B2A]/15 p-1 h-auto">
          <TabsTrigger value="excel" className="px-5 py-2 text-sm tracking-wide data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Bulk Excel
          </TabsTrigger>
          <TabsTrigger value="photos" className="px-5 py-2 text-sm tracking-wide data-[state=active]:bg-[#0D1B2A] data-[state=active]:text-white" data-testid="tab-photos">
            <ImagePlus className="w-4 h-4 mr-1.5" /> Bulk Photos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="excel">
          <ExcelUploadSection />
        </TabsContent>
        <TabsContent value="photos">
          <PhotoBulkSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ──────────────────────────── Excel section ────────────────────────────
function ExcelUploadSection() {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/catalog-products/template/download", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = "catalog_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    }
  };

  const doPreview = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/catalog-products/upload/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!preview || preview.rows.length === 0) return;
    if (!window.confirm(`Commit ${preview.rows.length} catalog product${preview.rows.length > 1 ? "s" : ""}?\n\n${preview.summary.new_count} new, ${preview.summary.update_count} update${preview.summary.update_count !== 1 ? "s" : ""}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/catalog-products/upload/commit", { rows: preview.rows });
      toast.success(`Imported: ${data.created} created · ${data.updated} updated${data.failed.length ? ` · ${data.failed.length} failed` : ""}`);
      setFile(null); setPreview(null);
      if (fileInput.current) fileInput.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadErrorCsv = () => {
    if (!preview?.errors?.length) return;
    const header = "Row,Product Code,Error\n";
    const rows = preview.errors.map((er) => `${er.row || ""},${csv(er.product_code || "")},${csv(er.error)}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `catalog_errors_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="space-y-5" data-testid="excel-section">
      <div className="luxe-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl text-[#0D1B2A]">Step 1 · Select your Excel file</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">Required column: <span className="font-mono">Product Code</span>. See the template for the full header row + continuation-row rule. Invalid rows are reported and skipped — they don&apos;t block the rest of the batch.</p>
          </div>
          <Button variant="outline" onClick={downloadTemplate} className="border-[#b8893b] text-[#b8893b] hover:bg-[#b8893b]/5" data-testid="dl-template">
            <Download className="w-4 h-4 mr-1.5" /> Download Excel template
          </Button>
        </div>
        <label className="block mt-5 cursor-pointer border-2 border-dashed border-[#0D1B2A]/20 rounded-md p-8 text-center hover:bg-[#FAF8F3] transition" data-testid="excel-drop">
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => doPreview(e.target.files?.[0])}
            className="hidden"
            data-testid="excel-file-input"
          />
          <FileSpreadsheet className="w-10 h-10 mx-auto text-[#b8893b] mb-2" />
          <p className="text-sm text-[#0D1B2A]">{file ? file.name : "Click to choose an .xlsx file"}</p>
          {file && <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>}
        </label>
      </div>

      {/* Preview */}
      {busy && !preview && <div className="luxe-card p-6"><div className="skeleton h-32" /></div>}

      {preview && (
        <div className="luxe-card p-6 space-y-4" data-testid="excel-preview">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-serif text-xl text-[#0D1B2A]">Step 2 · Preview &amp; confirm</h3>
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              <StatPill icon={CheckCircle2} colour="text-emerald-600" label="Valid" value={preview.summary.total_rows} testid="preview-total" />
              <StatPill colour="text-[#b8893b]" label="New" value={preview.summary.new_count} testid="preview-new" />
              <StatPill colour="text-blue-600" label="Updating" value={preview.summary.update_count} testid="preview-update" />
              <StatPill icon={AlertCircle} colour="text-red-600" label="Errors" value={preview.summary.error_count} testid="preview-errors" />
            </div>
          </div>

          {/* Table */}
          {preview.rows.length > 0 && (
            <div className="overflow-x-auto max-h-[400px] border rounded">
              <table className="w-full text-xs">
                <thead className="bg-[#0D1B2A] text-white sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Code</th>
                    <th className="px-2 py-1.5 text-left">Category</th>
                    <th className="px-2 py-1.5 text-right">Net Wt</th>
                    <th className="px-2 py-1.5 text-center">Purity</th>
                    <th className="px-2 py-1.5 text-right">Diamond</th>
                    <th className="px-2 py-1.5 text-center">DC</th>
                    <th className="px-2 py-1.5 text-left">Remark</th>
                  </tr>
                </thead>
                <tbody data-testid="excel-preview-rows">
                  {preview.rows.map((r) => (
                    <tr key={r.product_code} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-medium">{r.product_code}</td>
                      <td className="px-2 py-1.5">{r.category}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIndian(r.net_wt, { decimals: 3 })}</td>
                      <td className="px-2 py-1.5 text-center">{r.base_purity}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatIndian(r.diamond_wt, { decimals: 3 })}</td>
                      <td className="px-2 py-1.5 text-center">{r.distribution_catalog ? "Y" : "N"}</td>
                      <td className="px-2 py-1.5 text-slate-500 truncate max-w-[200px]">{r.remark}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 space-y-1 max-h-[160px] overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">{preview.errors.length} row{preview.errors.length > 1 ? "s" : ""} skipped:</span>
                <Button size="sm" variant="ghost" onClick={downloadErrorCsv} className="h-7 text-xs text-red-700">
                  <Download className="w-3 h-3 mr-1" /> Download error CSV
                </Button>
              </div>
              {preview.errors.slice(0, 12).map((e, i) => (
                <div key={`${e.row}-${e.product_code || i}`} data-testid={`excel-error-${i}`}>Row {e.row}{e.product_code ? ` · ${e.product_code}` : ""}: <span className="text-red-600">{e.error}</span></div>
              ))}
              {preview.errors.length > 12 && <div className="text-slate-500">…{preview.errors.length - 12} more — see CSV</div>}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-[#0D1B2A]/10">
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); if (fileInput.current) fileInput.current.value = ""; }} data-testid="preview-discard">Discard</Button>
            <Button onClick={doCommit} disabled={busy || preview.rows.length === 0} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="preview-commit">
              <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Importing…" : `Commit ${preview.rows.length} row${preview.rows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ──────────────────────────── Photo bulk section ────────────────────────────
function PhotoBulkSection() {
  const filesRef = useRef(null);
  const mapRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [mappingFile, setMappingFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (files.length === 0) return toast.error("Select at least one image file");
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (mappingFile) fd.append("mapping_file", mappingFile);
    try {
      const { data } = await api.post("/catalog-products/photos/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.matched_main} main · ${data.matched_extra} extras attached. ${data.unmatched.length} skipped.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFiles([]); setMappingFile(null); setResult(null);
    if (filesRef.current) filesRef.current.value = "";
    if (mapRef.current) mapRef.current.value = "";
  };

  return (
    <section className="space-y-5" data-testid="photo-section">
      <div className="luxe-card p-6 space-y-4">
        <div>
          <h3 className="font-serif text-xl text-[#0D1B2A]">Attach photos by Product Code</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Drag-drop or click to add multiple image files. Filenames must match the Product Code (e.g. <span className="font-mono">CCRG00001.jpg</span> → main photo, <span className="font-mono">CCRG00001_2.jpg</span> → extra photo, <span className="font-mono">_3</span>, <span className="font-mono">_4</span>, … for more extras). Allowed: jpg, jpeg, png, webp, heic, heif. Re-uploading <span className="font-mono">_1</span>-or-bare filename REPLACES the main photo.
          </p>
        </div>

        <label className="block cursor-pointer border-2 border-dashed border-[#0D1B2A]/20 rounded-md p-8 text-center hover:bg-[#FAF8F3] transition" data-testid="photo-drop">
          <input
            ref={filesRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="photo-files-input"
          />
          <ImagePlus className="w-10 h-10 mx-auto text-[#b8893b] mb-2" />
          <p className="text-sm text-[#0D1B2A]">{files.length === 0 ? "Click to select image files" : `${files.length} file${files.length > 1 ? "s" : ""} ready`}</p>
          {files.length > 0 && (
            <p className="text-xs text-slate-500 mt-1">{files.slice(0, 4).map((f) => f.name).join(", ")}{files.length > 4 ? ` …+${files.length - 4} more` : ""}</p>
          )}
        </label>

        {/* Optional mapping sheet */}
        <details className="border-t border-[#0D1B2A]/10 pt-3" data-testid="photo-mapping-toggle">
          <summary className="text-xs uppercase tracking-widest text-slate-500 cursor-pointer hover:text-[#b8893b]">
            Optional: Use a mapping sheet (for filenames that don&apos;t match product codes)
          </summary>
          <div className="pt-3 space-y-2">
            <p className="text-[11px] text-slate-500">CSV or Excel with columns: <span className="font-mono">Product Code</span> · <span className="font-mono">Photo File</span> · <span className="font-mono">Type</span> (main / extra).</p>
            <input
              ref={mapRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setMappingFile(e.target.files?.[0] || null)}
              className="text-xs"
              data-testid="photo-mapping-input"
            />
            {mappingFile && <p className="text-[11px] text-emerald-700">Mapping: {mappingFile.name}</p>}
          </div>
        </details>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#0D1B2A]/10">
          <Button variant="outline" onClick={reset} disabled={busy && files.length === 0}>Reset</Button>
          <Button onClick={upload} disabled={busy || files.length === 0} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="photo-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : `Upload ${files.length} file${files.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>

      {result && (
        <div className="luxe-card p-6 space-y-3" data-testid="photo-result">
          <h3 className="font-serif text-xl text-[#0D1B2A]">Upload summary</h3>
          <div className="grid grid-cols-3 gap-3">
            <ResultCard label="Main photos attached" value={result.matched_main} colour="emerald" testid="photo-summary-main" />
            <ResultCard label="Extra photos attached" value={result.matched_extra} colour="brand" testid="photo-summary-extras" />
            <ResultCard label="Unmatched files" value={result.unmatched.length} colour={result.unmatched.length === 0 ? "slate" : "red"} testid="photo-summary-unmatched" />
          </div>
          {result.unmatched.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 space-y-1 max-h-[200px] overflow-y-auto">
              <div className="font-semibold mb-1">Files skipped:</div>
              {result.unmatched.map((u, i) => (
                <div key={u.file || `unmatched-${i}`} data-testid={`photo-unmatched-${i}`}>
                  <span className="font-mono">{u.file}</span> — <span className="text-red-600">{u.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Tiny helpers
function StatPill({ icon: Icon, colour, label, value, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {Icon && <Icon className={`w-3.5 h-3.5 ${colour}`} />}
      <span className="uppercase tracking-wider">{label}:</span>
      <span className={`font-semibold tabular-nums ${colour}`}>{value}</span>
    </div>
  );
}

function ResultCard({ label, value, colour, testid }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    brand: "bg-[#b8893b]/10 border-[#b8893b]/30 text-[#b8893b]",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    red: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div className={`rounded-md border p-4 ${tones[colour] || tones.slate}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest opacity-80">{label}</p>
      <p className="text-3xl tabular-nums font-serif mt-1">{value}</p>
    </div>
  );
}

function csv(v) {
  if (v == null) return "";
  const s = String(v);
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
}
